'use client';

import { c as _c } from "react/compiler-runtime";
import type { CommentSide } from '@/components/inbox/prs/changes/types/comment';
import { ChevronDown, FileDiff, MessageCirclePlus, PaperclipIcon, SendHorizontal, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import type { TextareaVariant } from '../types';
import { TEXTAREA_VARIANTS } from '../types';
import { GifIcon } from '@/components/icons';
import { GifPicker } from '@/components/textarea/components/gif-picker';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { KeyboardShortcut } from '@/components/ui/keyboard-shortcut';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn, metaChar } from '@/lib/utils';
type TextareaButtonsProps = {
  hasText: boolean;
  onFileClickAction: () => void;
  handleGifSelectAction: (gif: {
    url: string;
    title: string;
  }) => void;
  showSendButton: boolean;
  variant: TextareaVariant;
  onAiSuggestion?: () => void;
  isLoadingAiSuggestion?: boolean;
  onGetCodeLine?: () => string | undefined;
  onSubmit?: () => void;
  onCancel?: () => void;
  onAddToReview?: () => void;
  disablePostImmediately?: boolean;
  textAreaRef?: React.RefObject<HTMLTextAreaElement | null>;
  /**
   * Additional buttons to render alongside the default buttons.
   * These will be rendered in the button group before the default buttons.
   */
  extraButtonNodes?: React.ReactNode;
  side?: CommentSide;
};
export type IconButtonProps = {
  onClick?: () => void;
  icon: React.ReactNode;
  label: string;
  tooltip: string;
  shortcut?: string;
  disabled?: boolean;
  className?: string;
};
export const IconButton = t0 => {
  const $ = _c(17);
  const {
    onClick,
    icon,
    label,
    tooltip,
    shortcut,
    disabled,
    className
  } = t0;
  const t1 = disabled && "animate-pulse cursor-not-allowed opacity-50 text-zinc-600 hover:text-zinc-600";
  let t2;
  if ($[0] !== className || $[1] !== t1) {
    t2 = cn("p-1 transition-colors text-zinc-400 hover:text-zinc-300 size-8", className, t1);
    $[0] = className;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== disabled || $[4] !== icon || $[5] !== label || $[6] !== onClick || $[7] !== t2) {
    t3 = <TooltipTrigger asChild={true}><Button type="button" variant="ghost" size="sm" className={t2} onClick={onClick} disabled={disabled} aria-label={label}>{icon}</Button></TooltipTrigger>;
    $[3] = disabled;
    $[4] = icon;
    $[5] = label;
    $[6] = onClick;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== shortcut) {
    t4 = shortcut && <KeyboardShortcut>{shortcut}</KeyboardShortcut>;
    $[9] = shortcut;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t4 || $[12] !== tooltip) {
    t5 = <TooltipContent><div className="flex items-center gap-2">{tooltip}{t4}</div></TooltipContent>;
    $[11] = t4;
    $[12] = tooltip;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t3 || $[15] !== t5) {
    t6 = <Tooltip>{t3}{t5}</Tooltip>;
    $[14] = t3;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
};
const SubmitButton = t0 => {
  const $ = _c(5);
  const {
    hasText,
    onSubmit
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <SendHorizontal className="size-4" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = metaChar();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = !hasText;
  let t4;
  if ($[2] !== onSubmit || $[3] !== t3) {
    t4 = <IconButton onClick={onSubmit} icon={t1} label="Submit" tooltip="Submit" shortcut={`${t2}↵`} disabled={t3} className="hover:text-zinc-300 disabled:text-zinc-600 disabled:hover:text-zinc-600 cursor-pointer" />;
    $[2] = onSubmit;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
};
const PrReplyButtons = t0 => {
  const $ = _c(53);
  const {
    hasText,
    onAddToReview,
    onSubmit,
    disablePostImmediately,
    variant
  } = t0;
  let T0;
  let T1;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== disablePostImmediately || $[1] !== hasText || $[2] !== onAddToReview || $[3] !== onSubmit || $[4] !== variant) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const baseButtonClass = cn("flex h-8 items-center gap-2 border border-input bg-transparent px-3 text-sm font-medium cursor-pointer", "ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground", "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2", "disabled:pointer-events-none disabled:opacity-50");
      if (variant === TEXTAREA_VARIANTS.prReply) {
        let t7;
        if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
          t7 = <SendHorizontal className="size-4" />;
          $[13] = t7;
        } else {
          t7 = $[13];
        }
        const t8 = <TooltipTrigger asChild={true}><button type="button" className={cn(baseButtonClass, "rounded-md")} aria-label="Submit" disabled={!hasText} onClick={onSubmit}>{t7}</button></TooltipTrigger>;
        let t9;
        if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
          t9 = <TooltipContent>Submit</TooltipContent>;
          $[14] = t9;
        } else {
          t9 = $[14];
        }
        let t10;
        if ($[15] !== t8) {
          t10 = <Tooltip>{t8}{t9}</Tooltip>;
          $[15] = t8;
          $[16] = t10;
        } else {
          t10 = $[16];
        }
        t6 = t10;
        break bb0;
      }
      if (disablePostImmediately) {
        let t7;
        if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
          t7 = <MessageCirclePlus className="size-4" />;
          $[17] = t7;
        } else {
          t7 = $[17];
        }
        const t8 = <TooltipTrigger asChild={true}><button type="button" className={cn(baseButtonClass, "rounded-md")} aria-label="Add to review" disabled={!hasText} onClick={onAddToReview}>{t7}</button></TooltipTrigger>;
        let t9;
        if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
          t9 = <TooltipContent>Add to review</TooltipContent>;
          $[18] = t9;
        } else {
          t9 = $[18];
        }
        let t10;
        if ($[19] !== t8) {
          t10 = <Tooltip>{t8}{t9}</Tooltip>;
          $[19] = t8;
          $[20] = t10;
        } else {
          t10 = $[20];
        }
        t6 = t10;
        break bb0;
      }
      t4 = "flex";
      let t7;
      if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = <MessageCirclePlus className="size-4" />;
        $[21] = t7;
      } else {
        t7 = $[21];
      }
      const t8 = <TooltipTrigger asChild={true}><button type="button" className={cn(baseButtonClass, "rounded-l-md border-r-0")} aria-label="Add to review" disabled={!hasText} onClick={onAddToReview}>{t7}</button></TooltipTrigger>;
      let t9;
      if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = <TooltipContent>Add to review</TooltipContent>;
        $[22] = t9;
      } else {
        t9 = $[22];
      }
      if ($[23] !== t8) {
        t5 = <Tooltip>{t8}{t9}</Tooltip>;
        $[23] = t8;
        $[24] = t5;
      } else {
        t5 = $[24];
      }
      T1 = DropdownMenu;
      T0 = DropdownMenuTrigger;
      t3 = true;
      t1 = "button";
      t2 = cn(baseButtonClass, "rounded-r-md pl-2 pr-1.5", "border-l border-l-input");
    }
    $[0] = disablePostImmediately;
    $[1] = hasText;
    $[2] = onAddToReview;
    $[3] = onSubmit;
    $[4] = variant;
    $[5] = T0;
    $[6] = T1;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    T0 = $[5];
    T1 = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  const t7 = !hasText;
  let t8;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <ChevronDown className="size-4" />;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== t1 || $[27] !== t2 || $[28] !== t7) {
    t9 = <button type={t1} className={t2} aria-label="Show comment options" disabled={t7}>{t8}</button>;
    $[26] = t1;
    $[27] = t2;
    $[28] = t7;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== T0 || $[31] !== t3 || $[32] !== t9) {
    t10 = <T0 asChild={t3}>{t9}</T0>;
    $[30] = T0;
    $[31] = t3;
    $[32] = t9;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <MessageCirclePlus className="mr-2 size-4" />;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <KeyboardShortcut>{`${metaChar()}↵`}</KeyboardShortcut>;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== onAddToReview) {
    t13 = <DropdownMenuItem onClick={onAddToReview}>{t11}Add to review{t12}</DropdownMenuItem>;
    $[36] = onAddToReview;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <SendHorizontal className="mr-2 size-4" />;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <KeyboardShortcut>{`${metaChar()}⇧↵`}</KeyboardShortcut>;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== onSubmit) {
    t16 = <DropdownMenuItem onClick={onSubmit}>{t14}Post immediately{t15}</DropdownMenuItem>;
    $[40] = onSubmit;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== t13 || $[43] !== t16) {
    t17 = <DropdownMenuContent align="end" className="w-fit">{t13}{t16}</DropdownMenuContent>;
    $[42] = t13;
    $[43] = t16;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== T1 || $[46] !== t10 || $[47] !== t17) {
    t18 = <T1>{t10}{t17}</T1>;
    $[45] = T1;
    $[46] = t10;
    $[47] = t17;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== t18 || $[50] !== t4 || $[51] !== t5) {
    t19 = <div className={t4}>{t5}{t18}</div>;
    $[49] = t18;
    $[50] = t4;
    $[51] = t5;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  return t19;
};
export function TextareaButtons(t0) {
  const $ = _c(79);
  const {
    hasText,
    onFileClickAction,
    handleGifSelectAction,
    showSendButton,
    variant,
    onAiSuggestion,
    isLoadingAiSuggestion,
    onGetCodeLine,
    onSubmit,
    onCancel,
    onAddToReview,
    disablePostImmediately: t1,
    textAreaRef,
    extraButtonNodes,
    side
  } = t0;
  const disablePostImmediately = t1 === undefined ? false : t1;
  const [isGifPickerOpen, setIsGifPickerOpen] = useState(false);
  const allowSuggestion = side !== "old";
  let t2;
  if ($[0] !== onGetCodeLine || $[1] !== textAreaRef) {
    t2 = () => {
      const textArea = textAreaRef?.current;
      if (textArea && onGetCodeLine) {
        const codeLine = onGetCodeLine();
        if (codeLine) {
          const text = textArea.value;
          const cursorPosition = textArea.selectionStart;
          const suggestionText = `\`\`\`suggestion\n${codeLine}\`\`\`\n`;
          const newText = text.substring(0, cursorPosition) + suggestionText + text.substring(cursorPosition);
          const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, "value")?.set;
          if (nativeInputValueSetter) {
            nativeInputValueSetter?.call(textArea, newText);
            textArea.dispatchEvent(new Event("input", {
              bubbles: true
            }));
          }
          textArea.focus();
          textArea.setSelectionRange(cursorPosition + suggestionText.length, cursorPosition + suggestionText.length);
        }
      }
    };
    $[0] = onGetCodeLine;
    $[1] = textAreaRef;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const insertSuggestionText = t2;
  let t3;
  if ($[3] !== isGifPickerOpen || $[4] !== textAreaRef) {
    t3 = e => {
      e.preventDefault();
      if (textAreaRef?.current === document.activeElement) {
        setIsGifPickerOpen(!isGifPickerOpen);
      }
    };
    $[3] = isGifPickerOpen;
    $[4] = textAreaRef;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      enableOnFormTags: true,
      preventDefault: true
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  useHotkeys("mod+shift+g", t3, t4);
  let t5;
  if ($[7] !== onFileClickAction || $[8] !== textAreaRef) {
    t5 = e_0 => {
      e_0.preventDefault();
      if (textAreaRef?.current === document.activeElement) {
        onFileClickAction();
      }
    };
    $[7] = onFileClickAction;
    $[8] = textAreaRef;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      enableOnFormTags: true,
      preventDefault: true
    };
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  useHotkeys("mod+shift+u", t5, t6);
  let t7;
  if ($[11] !== insertSuggestionText || $[12] !== onGetCodeLine || $[13] !== textAreaRef) {
    t7 = e_1 => {
      e_1.preventDefault();
      const textArea_0 = textAreaRef?.current;
      if (textArea_0 && textArea_0 === document.activeElement && onGetCodeLine) {
        insertSuggestionText();
      }
    };
    $[11] = insertSuggestionText;
    $[12] = onGetCodeLine;
    $[13] = textAreaRef;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const t8 = !!onGetCodeLine && allowSuggestion;
  let t9;
  if ($[15] !== t8) {
    t9 = {
      enableOnFormTags: true,
      preventDefault: true,
      enabled: t8
    };
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  useHotkeys("mod+g", t7, t9);
  const t10 = variant === TEXTAREA_VARIANTS.default;
  const t11 = variant === TEXTAREA_VARIANTS.reply;
  const t12 = variant === TEXTAREA_VARIANTS.prReply;
  const t13 = variant === TEXTAREA_VARIANTS.editingSavedContent;
  let t14;
  if ($[17] !== t10 || $[18] !== t11 || $[19] !== t12 || $[20] !== t13) {
    t14 = cn("flex gap-2", {
      "absolute bottom-4 right-3": t10,
      "absolute bottom-0.5 right-4": t11,
      "flex items-center justify-between": t12,
      "items-end justify-between": t13
    });
    $[17] = t10;
    $[18] = t11;
    $[19] = t12;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== allowSuggestion || $[23] !== isLoadingAiSuggestion || $[24] !== onAiSuggestion) {
    t15 = onAiSuggestion && allowSuggestion && <IconButton onClick={onAiSuggestion} icon={<Sparkles className="size-4" />} label="Generate code suggestion" tooltip={isLoadingAiSuggestion ? "Generating suggestion..." : "Generate code suggestion"} disabled={isLoadingAiSuggestion} />;
    $[22] = allowSuggestion;
    $[23] = isLoadingAiSuggestion;
    $[24] = onAiSuggestion;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== allowSuggestion || $[27] !== insertSuggestionText || $[28] !== onGetCodeLine) {
    t16 = onGetCodeLine && allowSuggestion && <IconButton onClick={insertSuggestionText} icon={<FileDiff className="size-4" />} disabled={!allowSuggestion} label="Add suggestion" tooltip="Add suggestion" shortcut={`${metaChar()}G`} />;
    $[26] = allowSuggestion;
    $[27] = insertSuggestionText;
    $[28] = onGetCodeLine;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <PaperclipIcon className="size-4" />;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = metaChar();
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== onFileClickAction) {
    t19 = <IconButton onClick={onFileClickAction} icon={t17} label="Attach" tooltip="Attach" shortcut={`${t18}⇧U`} />;
    $[32] = onFileClickAction;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = cn("p-1 size-8 stroke-zinc-400 transition-stroke transition-colors hover:stroke-zinc-300");
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <TooltipTrigger asChild={true}><PopoverTrigger asChild={true}><Button type="button" variant="ghost" size="sm" className={t20} aria-label="Add GIF"><GifIcon className="size-4 stroke-2  " /></Button></PopoverTrigger></TooltipTrigger>;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Tooltip>{t21}<TooltipContent><div className="flex items-center gap-2">Add GIF<KeyboardShortcut>{`${metaChar()}⇧G`}</KeyboardShortcut></div></TooltipContent></Tooltip>;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] !== handleGifSelectAction) {
    t23 = <PopoverContent align="end" side="top" className="h-[500px] w-96"><GifPicker handleGifSelectAction={handleGifSelectAction} /></PopoverContent>;
    $[37] = handleGifSelectAction;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  let t24;
  if ($[39] !== isGifPickerOpen || $[40] !== t23) {
    t24 = <Popover open={isGifPickerOpen} onOpenChange={setIsGifPickerOpen}>{t22}{t23}</Popover>;
    $[39] = isGifPickerOpen;
    $[40] = t23;
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  let t25;
  if ($[42] !== hasText || $[43] !== onSubmit || $[44] !== showSendButton || $[45] !== variant) {
    t25 = variant === TEXTAREA_VARIANTS.reply && showSendButton && <SubmitButton hasText={hasText} onSubmit={onSubmit} />;
    $[42] = hasText;
    $[43] = onSubmit;
    $[44] = showSendButton;
    $[45] = variant;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== extraButtonNodes || $[48] !== t15 || $[49] !== t16 || $[50] !== t19 || $[51] !== t24 || $[52] !== t25) {
    t26 = <div className="flex flex-row gap-1">{t15}{t16}{extraButtonNodes}{t19}{t24}{t25}</div>;
    $[47] = extraButtonNodes;
    $[48] = t15;
    $[49] = t16;
    $[50] = t19;
    $[51] = t24;
    $[52] = t25;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] !== onCancel || $[55] !== onSubmit || $[56] !== variant) {
    t27 = variant === TEXTAREA_VARIANTS.editingSavedContent && <><Button variant="ghost" size="sm" onClick={onCancel}>Cancel</Button><Button variant="secondary" size="sm" onClick={onSubmit}>Save</Button></>;
    $[54] = onCancel;
    $[55] = onSubmit;
    $[56] = variant;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  let t28;
  if ($[58] !== disablePostImmediately || $[59] !== hasText || $[60] !== onAddToReview || $[61] !== onSubmit || $[62] !== variant) {
    t28 = (variant === TEXTAREA_VARIANTS.prReply || variant === TEXTAREA_VARIANTS.prComment) && <PrReplyButtons hasText={hasText} onAddToReview={onAddToReview} onSubmit={onSubmit} disablePostImmediately={disablePostImmediately} variant={variant} />;
    $[58] = disablePostImmediately;
    $[59] = hasText;
    $[60] = onAddToReview;
    $[61] = onSubmit;
    $[62] = variant;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== hasText || $[65] !== onSubmit || $[66] !== showSendButton || $[67] !== variant) {
    t29 = variant !== TEXTAREA_VARIANTS.reply && variant !== TEXTAREA_VARIANTS.prReply && variant !== TEXTAREA_VARIANTS.prComment && showSendButton && <div className="flex gap-10"><SubmitButton hasText={hasText} onSubmit={onSubmit} /></div>;
    $[64] = hasText;
    $[65] = onSubmit;
    $[66] = showSendButton;
    $[67] = variant;
    $[68] = t29;
  } else {
    t29 = $[68];
  }
  let t30;
  if ($[69] !== t27 || $[70] !== t28 || $[71] !== t29) {
    t30 = <div className="flex flex-row gap-2">{t27}{t28}{t29}</div>;
    $[69] = t27;
    $[70] = t28;
    $[71] = t29;
    $[72] = t30;
  } else {
    t30 = $[72];
  }
  let t31;
  if ($[73] !== t26 || $[74] !== t30) {
    t31 = <TooltipProvider><div className="flex w-full items-end justify-between">{t26}{t30}</div></TooltipProvider>;
    $[73] = t26;
    $[74] = t30;
    $[75] = t31;
  } else {
    t31 = $[75];
  }
  let t32;
  if ($[76] !== t14 || $[77] !== t31) {
    t32 = <div className={t14}>{t31}</div>;
    $[76] = t14;
    $[77] = t31;
    $[78] = t32;
  } else {
    t32 = $[78];
  }
  return t32;
}